import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildHowToSchema,
  buildSoftwareApplicationSchema,
  toSeoProps,
  type JsonLdExpectation,
  type SeoRouteConfig,
  type UmbrellaSeoRoutes,
} from '@saas-pages/shared/public/seo'
import type { SeoProps } from '@saas-pages/shared/public/seo'
import branding from '../../branding.config'
import { enTranslations } from '../shared/i18n/en'
import { DEFAULT_LOCALE, type Locale } from '../shared/i18n/locale'
import { ruTranslations } from '../shared/i18n/ru'

export type RouteKey =
  | 'index'
  | 'onlinePaint'
  | 'imageConverter'
  | 'imageColorPicker'
  | 'cropImage'
  | 'rotateImage'
  | 'blurImage'
  | 'watermarkImage'
  | 'physicalPhotoSize'
  | 'photo3x4'
  | 'photoCollage'
  | 'imageGifMaker'
  | 'compressImage'
  | 'removeExif'
  | 'metadataViewer'
  | 'imageToWebp'
  | 'webpConverter'
  | 'avifConverter'
  | 'tiffToJpg'
  | 'svgConverter'
  | 'htmlToImage'
  | 'resizeImage'
  | 'socialMediaResize'
  | 'upscaleImage'
  | 'faviconGenerator'
  | 'heicViewer'
  | 'jpgViewer'
  | 'pngViewer'
  | 'webpViewer'
  | 'svgViewer'
  | 'avifViewer'
  | 'tiffViewer'
  | 'gifViewer'
  | 'bmpViewer'
  | 'icoViewer'
  | 'heicToJpg'
  | 'blog'

const rawSeoRoutes: UmbrellaSeoRoutes<RouteKey> = {
  domain: branding.domain,
  routes: {
    index: {
      path: '/',
      title: ruTranslations.seo.index.title,
      description: ruTranslations.seo.index.description,
      changefreq: 'weekly',
      priority: 1,
    },
    onlinePaint: {
      path: '/risovat-online',
      title: ruTranslations.seo.onlinePaint.title,
      description: ruTranslations.seo.onlinePaint.description,
      changefreq: 'monthly',
      priority: 1,
    },
    imageConverter: {
      path: '/konverter-izobrazheniy',
      title: ruTranslations.seo.imageConverter.title,
      description: ruTranslations.seo.imageConverter.description,
      changefreq: 'monthly',
      priority: 1,
    },
    imageColorPicker: {
      path: '/vybrat-cvet-s-kartinki',
      title: ruTranslations.seo.imageColorPicker.title,
      description: ruTranslations.seo.imageColorPicker.description,
      changefreq: 'monthly',
      priority: 1,
    },
    cropImage: {
      path: '/obrezat-foto',
      title: ruTranslations.seo.cropImage.title,
      description: ruTranslations.seo.cropImage.description,
      changefreq: 'monthly',
      priority: 1,
    },
    rotateImage: {
      path: '/povernut-foto',
      title: ruTranslations.seo.rotateImage.title,
      description: ruTranslations.seo.rotateImage.description,
      changefreq: 'monthly',
      priority: 1,
    },
    blurImage: {
      path: '/razmyt-foto',
      title: ruTranslations.seo.blurImage.title,
      description: ruTranslations.seo.blurImage.description,
      changefreq: 'monthly',
      priority: 1,
    },
    watermarkImage: {
      path: '/vodyanoj-znak-na-foto',
      title: ruTranslations.seo.watermarkImage.title,
      description: ruTranslations.seo.watermarkImage.description,
      changefreq: 'monthly',
      priority: 1,
    },
    photo3x4: {
      path: '/foto-3x4',
      title: ruTranslations.seo.photo3x4.title,
      description: ruTranslations.seo.photo3x4.description,
      changefreq: 'monthly',
      priority: 1,
    },
    photoCollage: {
      path: '/kollazh-iz-foto',
      title: ruTranslations.seo.photoCollage.title,
      description: ruTranslations.seo.photoCollage.description,
      changefreq: 'monthly',
      priority: 1,
    },
    imageGifMaker: {
      path: '/sozdat-gif-iz-foto',
      title: ruTranslations.seo.imageGifMaker.title,
      description: ruTranslations.seo.imageGifMaker.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    compressImage: {
      path: '/szhat-izobrazhenie',
      title: ruTranslations.seo.compressImage.title,
      description: ruTranslations.seo.compressImage.description,
      changefreq: 'monthly',
      priority: 1,
    },
    removeExif: {
      path: '/udalit-exif',
      title: ruTranslations.seo.removeExif.title,
      description: ruTranslations.seo.removeExif.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    metadataViewer: {
      path: '/posmotret-exif',
      title: ruTranslations.seo.metadataViewer.title,
      description: ruTranslations.seo.metadataViewer.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    physicalPhotoSize: {
      path: '/razmer-foto-v-sm',
      title: ruTranslations.seo.physicalPhotoSize.title,
      description: ruTranslations.seo.physicalPhotoSize.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    imageToWebp: {
      path: '/v-webp',
      title: ruTranslations.seo.imageToWebp.title,
      description: ruTranslations.seo.imageToWebp.description,
      changefreq: 'monthly',
      priority: 1,
    },
    webpConverter: {
      path: '/webp-v-jpg-png',
      title: ruTranslations.seo.webpConverter.title,
      description: ruTranslations.seo.webpConverter.description,
      changefreq: 'monthly',
      priority: 1,
    },
    avifConverter: {
      path: '/avif-konverter',
      title: ruTranslations.seo.avifConverter.title,
      description: ruTranslations.seo.avifConverter.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    tiffToJpg: {
      path: '/tiff-v-jpg',
      title: ruTranslations.seo.tiffToJpg.title,
      description: ruTranslations.seo.tiffToJpg.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    svgConverter: {
      path: '/svg-v-png-jpg',
      title: ruTranslations.seo.svgConverter.title,
      description: ruTranslations.seo.svgConverter.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    htmlToImage: {
      path: '/html-v-kartinku',
      title: ruTranslations.seo.htmlToImage.title,
      description: ruTranslations.seo.htmlToImage.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    resizeImage: {
      path: '/izmenit-razmer-foto',
      title: ruTranslations.seo.resizeImage.title,
      description: ruTranslations.seo.resizeImage.description,
      changefreq: 'monthly',
      priority: 1,
    },
    socialMediaResize: {
      path: '/razmer-foto-dlya-socsetey',
      title: ruTranslations.seo.socialMediaResize.title,
      description: ruTranslations.seo.socialMediaResize.description,
      changefreq: 'monthly',
      priority: 1,
    },
    upscaleImage: {
      path: '/uvelichit-foto',
      title: ruTranslations.seo.upscaleImage.title,
      description: ruTranslations.seo.upscaleImage.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    faviconGenerator: {
      path: '/favicon-generator',
      title: ruTranslations.seo.faviconGenerator.title,
      description: ruTranslations.seo.faviconGenerator.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    heicViewer: {
      path: '/otkryt-heic',
      title: ruTranslations.seo.heicViewer.title,
      description: ruTranslations.seo.heicViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    jpgViewer: {
      path: '/otkryt-jpg',
      title: ruTranslations.seo.jpgViewer.title,
      description: ruTranslations.seo.jpgViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    pngViewer: {
      path: '/otkryt-png',
      title: ruTranslations.seo.pngViewer.title,
      description: ruTranslations.seo.pngViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    webpViewer: {
      path: '/otkryt-webp',
      title: ruTranslations.seo.webpViewer.title,
      description: ruTranslations.seo.webpViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    svgViewer: {
      path: '/otkryt-svg',
      title: ruTranslations.seo.svgViewer.title,
      description: ruTranslations.seo.svgViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    avifViewer: {
      path: '/otkryt-avif',
      title: ruTranslations.seo.avifViewer.title,
      description: ruTranslations.seo.avifViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    tiffViewer: {
      path: '/otkryt-tiff',
      title: ruTranslations.seo.tiffViewer.title,
      description: ruTranslations.seo.tiffViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    gifViewer: {
      path: '/otkryt-gif',
      title: ruTranslations.seo.gifViewer.title,
      description: ruTranslations.seo.gifViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    bmpViewer: {
      path: '/otkryt-bmp',
      title: ruTranslations.seo.bmpViewer.title,
      description: ruTranslations.seo.bmpViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    icoViewer: {
      path: '/otkryt-ico',
      title: ruTranslations.seo.icoViewer.title,
      description: ruTranslations.seo.icoViewer.description,
      changefreq: 'monthly',
      priority: 1,
    },
    heicToJpg: {
      path: '/heic-v-jpg',
      title: ruTranslations.seo.heicToJpg.title,
      description: ruTranslations.seo.heicToJpg.description,
      changefreq: 'monthly',
      priority: 0.9,
    },
    blog: {
      path: '/blog',
      title: ruTranslations.seo.blog.title,
      description: ruTranslations.seo.blog.description,
      changefreq: 'weekly',
      priority: 0.7,
    },
  },
}

/** Source-owned JSON-LD for every indexable tool route. Runtime pages only render this metadata. */
export const seoRoutes: UmbrellaSeoRoutes<RouteKey> = {
  ...rawSeoRoutes,
  routes: Object.fromEntries(Object.entries(rawSeoRoutes.routes).map(([key, route]) => [
    key,
    { ...route, jsonLd: sourceJsonLd(key as RouteKey, route, ruTranslations) },
  ])) as UmbrellaSeoRoutes<RouteKey>['routes'],
}

export function seoFor(key: RouteKey, locale: Locale = DEFAULT_LOCALE): SeoProps {
  if (locale === 'en') {
    const route = {
      ...seoRoutes.routes[key],
      ...enTranslations.seo[key],
    }
    return toSeoProps(seoRoutes.domain, {
      ...route,
      jsonLd: sourceJsonLd(key, route, enTranslations),
    }, { locale: 'en' })
  }

  return toSeoProps(seoRoutes.domain, seoRoutes.routes[key])
}

function sourceJsonLd(
  key: RouteKey,
  route: SeoRouteConfig,
  translations: unknown,
): JsonLdExpectation {
  if (key === 'index' || key === 'blog') {
    return { mode: 'none', reason: 'This index route does not represent an individual software tool.' }
  }

  const canonical = `https://${branding.domain}${route.path}`
  const copy = toolCopyForRoute(key, translations)
  const schemas: object[] = [
    buildBreadcrumbSchema([
      { name: branding.domain, url: `https://${branding.domain}/` },
      { name: route.title, url: canonical },
    ]),
    buildSoftwareApplicationSchema({
      name: route.title,
      description: route.description,
      category: 'UtilitiesApplication',
      url: canonical,
    }),
  ]
  if (copy.howTo.length > 0) {
    schemas.push(buildHowToSchema(route.title, copy.howTo))
  }
  if (copy.faq.length > 0) {
    schemas.push(buildFaqSchema(copy.faq))
  }
  return { mode: 'required', schemas }
}

export function toolCopyForRoute(key: RouteKey, translations: unknown): {
  howTo: readonly string[]
  faq: readonly { question: string; answer: string }[]
} {
  const keyByRoute: Partial<Record<RouteKey, string>> = {
    onlinePaint: 'onlinePaint',
    imageConverter: 'converter',
    imageColorPicker: 'imageColorPicker',
    cropImage: 'crop',
    rotateImage: 'rotateImage',
    blurImage: 'blurImage',
    watermarkImage: 'watermarkImage',
    photo3x4: 'photo3x4',
    photoCollage: 'photoCollage',
    imageGifMaker: 'imageGifMaker',
    compressImage: 'compressImage',
    removeExif: 'removeExif',
    metadataViewer: 'metadataViewer',
    imageToWebp: 'imageToWebp',
    webpConverter: 'webpConverter',
    physicalPhotoSize: 'physicalPhotoSize',
    avifConverter: 'avifConverter',
    tiffToJpg: 'tiffToJpg',
    faviconGenerator: 'faviconGenerator',
    heicViewer: 'heicViewer',
    jpgViewer: 'jpgViewer',
    pngViewer: 'pngViewer',
    svgViewer: 'svgViewer',
    avifViewer: 'avifViewer',
    tiffViewer: 'tiffViewer',
    webpViewer: 'webpViewer',
    gifViewer: 'gifViewer',
    bmpViewer: 'bmpViewer',
    icoViewer: 'icoViewer',
    heicToJpg: 'heicToJpg',
    svgConverter: 'svgConverter',
    htmlToImage: 'htmlToImage',
    resizeImage: 'resize',
    socialMediaResize: 'socialMediaResize',
    upscaleImage: 'upscale',
  }
  const dictionary = translations as Record<string, unknown>
  const copy = key === 'imageConverter'
    ? nestedCopy(dictionary, ['converter', 'page'])
    : keyByRoute[key]
      ? dictionary[keyByRoute[key]!]
      : undefined
  if (!copy || typeof copy !== 'object') return { howTo: [], faq: [] }
  const record = copy as Record<string, unknown>
  return {
    howTo: stringItems(record.howTo),
    faq: faqItems(record.faq),
  }
}

function nestedCopy(source: Record<string, unknown>, keys: readonly string[]): unknown {
  return keys.reduce<unknown>((current, key) =>
    current && typeof current === 'object' ? (current as Record<string, unknown>)[key] : undefined,
  source)
}

function stringItems(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function faqItems(value: unknown): Array<{ question: string; answer: string }> {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return []
    const record = item as Record<string, unknown>
    const question = typeof record.question === 'string' ? record.question : record.q
    const answer = typeof record.answer === 'string' ? record.answer : record.a
    return typeof question === 'string' && typeof answer === 'string'
      ? [{ question, answer }]
      : []
  })
}
